// Port of ImageDisplay() in android/.../ui/MainActivity.kt.
// Shows an <img>, starts the rotation timer only after the image actually
// loads (so a slow image doesn't get its slot eaten by network latency),
// retries exactly once with a cache-buster on error, then surrenders the
// slot and reports the error upstream so the dashboard can flag it.
//
// `crossOrigin="anonymous"` is required for the Service Worker (Phase 5) to
// cache a non-opaque response from the S3 bucket. The bucket CORS already
// allows web.wetook.net + the dev origin.

import { useEffect, useRef, useState } from 'react'
import { absoluteUrl } from '../lib/contentUrl'
import ContentErrorScreen from './ContentErrorScreen'

interface Props {
  url:        string
  durationMs: number
  scaling?:   'default' | 'fit'  // 'default'=contain (letterbox); 'fit'=cover (fill)
  onFinished: () => void
  onError:    (msg: string) => void
  onRetry:    (url: string) => void
}

export default function ImageDisplay({ url, durationMs, scaling = 'default', onFinished, onError, onRetry }: Props) {
  const fullUrl = absoluteUrl(url)
  const [attempt,  setAttempt]  = useState(0)
  const [loaded,   setLoaded]   = useState(false)
  const [hasError, setHasError] = useState(false)
  const retriedRef = useRef(false)
  const finishedRef = useRef(false)

  const advance = () => {
    if (finishedRef.current) return
    finishedRef.current = true
    onFinished()
  }

  // Start the rotation timer once the image is on screen.
  useEffect(() => {
    if (!loaded) return
    const t = window.setTimeout(advance, durationMs)
    return () => window.clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, durationMs])

  // Safety valve: never block rotation by more than configured + 30s.
  useEffect(() => {
    const t = window.setTimeout(advance, durationMs + 30_000)
    return () => window.clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullUrl, durationMs])

  if (hasError) return <ContentErrorScreen />

  // Cache-bust on retry; first attempt requests the canonical URL so the
  // Service Worker (Phase 5) can serve it from cache.
  const src = attempt === 0 ? fullUrl : `${fullUrl}${fullUrl.includes('?') ? '&' : '?'}_retry=${attempt}`

  return (
    <div className="absolute inset-0 bg-black flex items-center justify-center">
      <img
        src={src}
        alt=""
        crossOrigin="anonymous"
        className={`w-full h-full ${scaling === 'fit' ? 'object-cover' : 'object-contain'}`}
        onLoad={() => setLoaded(true)}
        onError={() => {
          if (!retriedRef.current) {
            retriedRef.current = true
            onRetry(fullUrl)
            setAttempt(a => a + 1)
          } else {
            setHasError(true)
            setLoaded(true)              // advance even on error
            onError(`Failed to load image: ${fullUrl}`)
          }
        }}
      />
      {/* Opaque overlay hides any first-frame flash; fades out once loaded.
          Mirrors the AnimatedVisibility(exit = fadeOut()) pattern in Compose. */}
      {!loaded && <div className="absolute inset-0 bg-black" />}
    </div>
  )
}
